import useDateLocale from '@/common/hooks/useDateLocale'
import { ShareBoard } from '@/meeting/meetingSummaryShareBoard'
import {
  Badge,
  Button,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react'
import {
  AdminReleaseFragment,
  useAdminUpdateReleaseItemsMutation,
  useAdminUpdateReleaseMutation,
} from '@gql'
import { format } from 'date-fns'
import debounce from 'lodash.debounce'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import AdminReleaseBoard from '../components/AdminReleaseBoard'
import { boardFromItems, canPublish, itemUpdates } from '../releaseBoard'

interface Props {
  release: AdminReleaseFragment
  onPublishedChange(publishedAt: string | null): void
}

export default function AdminReleaseEditor({
  release,
  onPublishedChange,
}: Props) {
  const { t } = useTranslation()
  const toast = useToast()
  const dateLocale = useDateLocale()
  const [updateRelease, { loading: publishing }] =
    useAdminUpdateReleaseMutation()
  const [updateItems] = useAdminUpdateReleaseItemsMutation()

  const onError = (error: any) =>
    toast({
      title: t('common.error'),
      description: error?.message,
      status: 'error',
    })

  // --- Title: saved when leaving the field
  const [title, setTitle] = useState(release.title)
  const saveTitle = () => {
    if (title.trim() === release.title) return
    updateRelease({
      variables: { version: release.version, values: { title: title.trim() } },
    }).catch(onError)
  }

  // --- Board: the local copy leads while a write is pending, so a move is
  // not undone by the subscription answering in between.
  const [board, setBoard] = useState<ShareBoard>(() =>
    boardFromItems(release.items)
  )
  const pending = useRef(false)
  useEffect(() => {
    if (!pending.current) setBoard(boardFromItems(release.items))
  }, [release.items])

  const save = useMemo(
    () =>
      debounce((next: ShareBoard, items: AdminReleaseFragment['items']) => {
        const updates = itemUpdates(next, items)
        if (updates.length === 0) {
          pending.current = false
          return
        }
        updateItems({
          variables: {
            updates: updates.map(({ id, ...values }) => ({
              where: { id: { _eq: id } },
              _set: values,
            })),
          },
        })
          .catch(onError)
          .finally(() => (pending.current = false))
      }, 500),
    []
  )
  useEffect(() => () => save.flush(), [save])

  const handleChange = (next: ShareBoard) => {
    pending.current = true
    setBoard(next)
    save(next, release.items)
  }

  const setPublication = (publishedAt: string | null) =>
    updateRelease({
      variables: {
        version: release.version,
        values: { title: title.trim(), publishedAt },
      },
    })
      .then(({ data }) =>
        onPublishedChange(data?.update_release_by_pk?.publishedAt ?? null)
      )
      .catch(onError)

  return (
    <VStack align="stretch" spacing={5}>
      <Flex gap={3} align="end" wrap="wrap">
        <FormControl maxW="560px">
          <FormLabel>{t('SuperAdmin.releases.title')}</FormLabel>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={saveTitle}
          />
          <FormHelperText>{t('SuperAdmin.releases.titleHelp')}</FormHelperText>
        </FormControl>

        <Flex ml="auto" direction="column" align="end" gap={1}>
          {release.publishedAt ? (
            <>
              <Badge colorScheme="green">
                {t('SuperAdmin.releases.publishedOn', {
                  date: format(new Date(release.publishedAt), 'PPp', {
                    locale: dateLocale,
                  }),
                })}
              </Badge>
              <Button
                size="xs"
                variant="ghost"
                isLoading={publishing}
                onClick={() => setPublication(null)}
              >
                {t('SuperAdmin.releases.unpublish')}
              </Button>
            </>
          ) : (
            <>
              <Button
                colorScheme="purple"
                isDisabled={!canPublish(board, title)}
                isLoading={publishing}
                onClick={() => setPublication(new Date().toISOString())}
              >
                {t('SuperAdmin.releases.publish')}
              </Button>
              {!canPublish(board, title) && (
                <Text fontSize="xs" color="gray.500">
                  {t('SuperAdmin.releases.publishNeeds')}
                </Text>
              )}
            </>
          )}
        </Flex>
      </Flex>

      <AdminReleaseBoard
        items={release.items}
        board={board}
        onChange={handleChange}
      />
    </VStack>
  )
}
